import React, { useState, useEffect } from 'react';
import { Form } from '../../../ui/universe';
import { TagInput, Tooltip, Checkbox, Input } from 'tdesign-react';
import { splitByCommas } from '../../../../utils/index.js';
import '../../../../style/RecognizeAgent.css';

/**
 * RecognizeAgent 任务类型选择 + 动态参数渲染器
 * 包含：任务类型卡片选择 + 根据类型动态显示的输入区域（Query、坐标等）
 */
function RecognizeAgentTaskTypeRenderer({ params, value, onChange, allParamsValue, onParamsChange }) {
  const [inputValue, setInputValue] = useState('');
  const [tags, setTags] = useState([]);

  const queryType = value;
  const queryValue = allParamsValue?.Query || [];
  const enableCoord = allParamsValue?.EnableCoord || false;

  // 切换任务类型时重置输入
  useEffect(() => {
    setInputValue('');
    setTags([]);
    // 清空 Query 和 EnableCoord
    onParamsChange && onParamsChange({ Query: [], EnableCoord: false });
  }, [queryType]);

  const handleQueryChange = (newQuery) => {
    onParamsChange && onParamsChange({ Query: newQuery });
  };

  const handleCoordChange = (val) => {
    onParamsChange && onParamsChange({ EnableCoord: val });
  };

  // 渲染某个任务类型对应的动态参数（紧挨在卡片下方）
  const renderInlineParams = (optionValue) => {
    if (value !== optionValue) return null;

    if (optionValue === 1) {
      return (
        <div className="recognize-agent-inline-params">
          <div className="recognize-agent-dynamic-field__label">
            <span>判断内容</span>
            <Tooltip content="判断内容数量仅为1" theme="dark">
              <span className="ocrDemo-icon-question"></span>
            </Tooltip>
          </div>
          <TagInput
            value={tags}
            placeholder="请输入判断内容，如：增值税专用发票，回车确认"
            excessTagsDisplayType="break-line"
            size="medium"
            valueDisplay={({ value: displayTags, onClose }) =>
              displayTags?.map((item, index) => (
                <span key={index} className="recognize-agent-tag">
                  <span>{item}</span>
                  <span
                    className="recognize-agent-tag__close"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(index);
                      const newTags = tags.filter((_, i) => i !== index);
                      setTags(newTags);
                      handleQueryChange(newTags);
                    }}
                  >×</span>
                </span>
              ))
            }
            onChange={(newTags, cur) => {
              if (cur?.e?.keyCode === 8 || cur?.e?.keyCode === 46) {
                const updatedTags = newTags || [];
                setTags(updatedTags);
                handleQueryChange(updatedTags);
                return;
              }
              if (!newTags.length) return;
              const _curItems = splitByCommas(cur.item);
              const allTags = [...tags, ..._curItems];
              setTags(allTags);
              handleQueryChange(allTags);
            }}
            onClear={() => {
              setTags([]);
              handleQueryChange([]);
            }}
            clearable
          />
        </div>
      );
    }

    if (optionValue === 2) {
      return (
        <div className="recognize-agent-inline-params">
          <div className="recognize-agent-dynamic-field__label">
            <span>分类项（最多5个）</span>
            <Tooltip content="分类项数量必须在2-5个之间" theme="dark">
              <span className="ocrDemo-icon-question"></span>
            </Tooltip>
          </div>
          <TagInput
            value={tags}
            placeholder="请输入分类项，如：营业执照，回车确认"
            excessTagsDisplayType="break-line"
            size="medium"
            valueDisplay={({ value: displayTags, onClose }) =>
              displayTags?.map((item, index) => (
                <span key={index} className="recognize-agent-tag">
                  <span>{item}</span>
                  <span
                    className="recognize-agent-tag__close"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(index);
                      const newTags = tags.filter((_, i) => i !== index);
                      setTags(newTags);
                      handleQueryChange(newTags);
                    }}
                  >×</span>
                </span>
              ))
            }
            onChange={(newTags, cur) => {
              if (cur?.e?.keyCode === 8 || cur?.e?.keyCode === 46) {
                const updatedTags = newTags || [];
                setTags(updatedTags);
                handleQueryChange(updatedTags);
                return;
              }
              if (!newTags.length) return;
              const _curItems = splitByCommas(cur.item);
              const allTags = [...tags, ..._curItems].slice(0, 5);
              setTags(allTags);
              handleQueryChange(allTags);
            }}
            onClear={() => {
              setTags([]);
              handleQueryChange([]);
            }}
            clearable
          />
        </div>
      );
    }

    if (optionValue === 3) {
      return (
        <div className="recognize-agent-inline-params">
          <div className="recognize-agent-dynamic-field__label">总结主题</div>
          <Input
            placeholder="请输入总结主题，如：工作经历"
            value={inputValue}
            onChange={(val) => {
              setInputValue(val);
              handleQueryChange(val ? [val] : []);
            }}
          />
        </div>
      );
    }

    if (optionValue === 4) {
      const handleSchemaChange = (fieldNames) => {
        const schemaItems = fieldNames.map((name) => ({
          KeyName: name,
          KeyType: 0,
          KeyPrompt: ''
        }));
        onParamsChange && onParamsChange({ SchemaItems: schemaItems, Query: [] });
      };

      return (
        <div className="recognize-agent-inline-params">
          <div className="recognize-agent-dynamic-field__label">
            <span>自定义提取字段</span>
            <Tooltip content="输入字段名，多个字段用逗号分隔" theme="dark">
              <span className="ocrDemo-icon-question"></span>
            </Tooltip>
          </div>
          <TagInput
            value={tags}
            placeholder="输入字段名，多个字段用逗号分隔"
            excessTagsDisplayType="break-line"
            size="medium"
            valueDisplay={({ value: displayTags, onClose }) =>
              displayTags?.map((item, index) => (
                <span key={index} className="recognize-agent-tag">
                  <span>{item}</span>
                  <span
                    className="recognize-agent-tag__close"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(index);
                      const newTags = tags.filter((_, i) => i !== index);
                      setTags(newTags);
                      handleSchemaChange(newTags);
                    }}
                  >×</span>
                </span>
              ))
            }
            onChange={(newTags, cur) => {
              if (cur?.e?.keyCode === 8 || cur?.e?.keyCode === 46) {
                const updatedTags = newTags || [];
                setTags(updatedTags);
                handleSchemaChange(updatedTags);
                return;
              }
              if (!newTags.length) return;
              const _curItems = splitByCommas(cur.item);
              const allTags = [...tags, ..._curItems];
              setTags(allTags);
              handleSchemaChange(allTags);
            }}
            onClear={() => {
              setTags([]);
              handleSchemaChange([]);
            }}
            clearable
          />
          <div style={{ marginTop: '8px' }}>
            <Checkbox
              checked={enableCoord}
              onChange={handleCoordChange}
            >
              返回坐标信息
            </Checkbox>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <Form.Item className="column" label={params.label}>
        <div className="recognize-agent-task-type-list">
          {params.options?.map((option) => (
            <React.Fragment key={option.value}>
              <div
                className={`recognize-agent-task-type-item ${value === option.value ? 'active' : ''}`}
                onClick={() => onChange && onChange(option.value, option.value, option)}
              >
                <span className="recognize-agent-task-type-item__title">{option.text}</span>
                <span className="recognize-agent-task-type-item__des">{option.des}</span>
              </div>
              {renderInlineParams(option.value)}
            </React.Fragment>
          ))}
        </div>
      </Form.Item>
    </>
  );
}

export {
  RecognizeAgentTaskTypeRenderer
};
